import { useEffect, useMemo, useRef, useState } from 'react'
import Scene from './canvas/Scene'
import Sidebar from './components/Sidebar'
import Details from './components/Details'
import Timeline from './components/Timeline'
import { ObjectData, InterstellarObject } from './lib/types'
import { api } from './lib/api'
import { Header } from './components/Header'
import Toast from './components/Toast'
import { useStore } from './lib/store'

const DAY_MS = 86400000

const PLANET_WIKI: Record<string, string> = {
  SUN: 'https://en.wikipedia.org/wiki/Sun',
  MERCURY: 'https://en.wikipedia.org/wiki/Mercury_(planet)',
  VENUS: 'https://en.wikipedia.org/wiki/Venus',
  EARTH: 'https://en.wikipedia.org/wiki/Earth',
  MARS: 'https://en.wikipedia.org/wiki/Mars',
  JUPITER: 'https://en.wikipedia.org/wiki/Jupiter',
  SATURN: 'https://en.wikipedia.org/wiki/Saturn',
  URANUS: 'https://en.wikipedia.org/wiki/Uranus',
  NEPTUNE: 'https://en.wikipedia.org/wiki/Neptune',
  PLUTO: 'https://en.wikipedia.org/wiki/Pluto',
}

type PlanetElements = {
  a: number
  e: number
  i: number
  omega: number
  w: number
  M: number
  epoch_jd: number
  q?: number | null
}

type ObjectState = {
  data: ObjectData | null
  loading: boolean
  error: string | null
}

function parseHash(): string | null {
  if (typeof window === 'undefined') return null
  const match = window.location.hash.match(/^#\/object\/(.+)$/)
  return match ? decodeURIComponent(match[1]) : null
}

export default function App() {
  const [isoItems, setIsoItems] = useState<InterstellarObject[]>([])
  const [isoLoading, setIsoLoading] = useState(true)
  const [isoError, setIsoError] = useState<string | null>(null)
  const [objectState, setObjectState] = useState<ObjectState>({
    data: null,
    loading: false,
    error: null,
  })
  const objectCache = useRef<Record<string, ObjectData>>({})

  const selectedId = useStore((s) => s.selectedId)
  const setSelectedId = useStore((s) => s.setSelectedId)
  const timeMs = useStore((s) => s.timeMs)
  const setTimeMs = useStore((s) => s.setTimeMs)
  const playing = useStore((s) => s.playing)
  const setPlaying = useStore((s) => s.setPlaying)
  const setObjectUpdated = useStore((s) => s.setObjectUpdated)
  const pushToast = useStore((s) => s.pushToast)
  const [isoReloadKey, setIsoReloadKey] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [focusedPlanet, setFocusedPlanet] = useState<string | null>(null)
  const [planets, setPlanets] = useState<Record<string, PlanetElements> | null>(null)
const [closest, setClosest] = useState<Array<{ id: string; name: string; date?: string; dist_ld?: number | null }>>([])

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (media.matches) setPlaying(false)
    const handler = (event: MediaQueryListEvent) => {
      if (event.matches) setPlaying(false)
    }
    if (media.addEventListener) media.addEventListener('change', handler)
    else media.addListener(handler)
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', handler)
      else media.removeListener(handler)
    }
  }, [setPlaying])

  useEffect(() => {
    const applyHash = () => {
      const fromHash = parseHash()
      if (fromHash) {
        setSelectedId(fromHash)
      }
    }
    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => window.removeEventListener('hashchange', applyHash)
  }, [setSelectedId])

  useEffect(() => {
    let cancelled = false
    setIsoLoading(true)
    setIsoError(null)
    api
      .interstellar()
      .then((items) => {
        if (cancelled) return
        setIsoItems(items)
        setIsoLoading(false)
        const current = useStore.getState().selectedId
        const initial = parseHash()
        const atlas = items.find(
          (item) =>
            /3i/i.test(item.id ?? '') ||
            /3i/i.test(item.name ?? '') ||
            /atlas/i.test(item.name ?? '') ||
            /2025\s*N1/i.test(item.id ?? ''),
        )
        const fallback = items[0]?.id ?? null
        const nextId = initial || atlas?.id || fallback
        const exists = nextId ? items.some((item) => item.id === nextId) : false
        if ((!current || !items.some((item) => item.id === current)) && nextId && exists) {
          setFocusedPlanet(null)
          setSelectedId(nextId)
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'Unable to load the interstellar list.'
        setIsoError(message)
        setIsoLoading(false)
        pushToast({ message, tone: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [setSelectedId, pushToast, isoReloadKey])

  useEffect(() => {
    if (!selectedId) return
    const cached = objectCache.current[selectedId]
    setObjectState({
      data: cached ?? null,
      loading: !cached,
      error: null,
    })

    let cancelled = false
    api
      .object(selectedId)
      .then((data) => {
        if (cancelled) return
        objectCache.current[selectedId] = data
        setObjectState({ data, loading: false, error: null })
        setObjectUpdated(selectedId, Date.now())
        setErrorMsg(null)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'Unable to load object details.'
        setObjectState((prev) => ({
          data: prev.data ?? cached ?? null,
          loading: false,
          error: message,
        }))
        setErrorMsg(message)
      })

    return () => {
      cancelled = true
    }
  }, [selectedId, setObjectUpdated, pushToast])

  useEffect(() => {
    if (!selectedId) return
    const next = `#/object/${encodeURIComponent(selectedId)}`
    if (window.location.hash !== next) window.location.hash = next
  }, [selectedId])

  useEffect(() => {
    const jd = Date.now() / 86400000 + 2440587.5
    api
      .planets(jd)
      .then((data) => setPlanets(data))
      .catch((error) => {
        console.error('planets fetch failed', error)
      })
    api
      .closest()
      .then((payload) => setClosest(payload.items ?? []))
      .catch((error) => {
        console.error('closest fetch failed', error)
      })
  }, [])

  const playAnchorRef = useRef(timeMs)

  useEffect(() => {
    if (!playing) return
    playAnchorRef.current = timeMs
    const SPEED_DAYS_PER_SECOND = 3
    const min = playAnchorRef.current - 180 * DAY_MS
    const max = playAnchorRef.current + 180 * DAY_MS

    let frameId = 0
    let last = performance.now()

    const tick = (time: number) => {
      const deltaSeconds = (time - last) / 1000
      last = time
      const advance = deltaSeconds * SPEED_DAYS_PER_SECOND * DAY_MS
      setTimeMs((prev) => {
        let next = prev + advance
        if (next > max) next = min
        if (next < min) next = max
        return next
      })
      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame((time) => {
      last = time
      tick(time)
    })

    return () => cancelAnimationFrame(frameId)
  }, [playing, setTimeMs, timeMs])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase()
      if (key === 'escape') {
        setDetailsOpen(false)
      }
      if (key === 'i' || key === '?') {
        setDetailsOpen((value) => !value)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const sidebarItems = useMemo(() => isoItems, [isoItems])

  const handleSelect = (id: string) => {
    setFocusedPlanet(null)
    setSelectedId(id)
    setDetailsOpen(false)
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setDrawerOpen(false)
    }
  }

  const handleSelectPlanet = (pid: string) => {
    setFocusedPlanet(pid)
    setSelectedId(null)
    setObjectState({ data: null, loading: false, error: null })
    setErrorMsg(null)
    setDetailsOpen(false)
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setDrawerOpen(false)
    }
  }

  const handleSelectClosest = (id: string) => {
    setFocusedPlanet(null)
    setSelectedId(id)
    setDetailsOpen(false)
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setDrawerOpen(false)
    }
  }

  const handleSceneBackgroundClick = () => {
    setDetailsOpen(false)
  }

  const detailsTargetId = selectedId ?? focusedPlanet
  const detailsWiki = (() => {
    if (!detailsTargetId) return undefined
    if (selectedId) {
      return objectState.data?.wikipedia
        ?? `https://en.wikipedia.org/wiki/${encodeURIComponent(detailsTargetId.replace(/\s+/g, '_'))}`
    }
    if (focusedPlanet) {
      return PLANET_WIKI[focusedPlanet] ?? `https://en.wikipedia.org/wiki/${encodeURIComponent(focusedPlanet.replace(/\s+/g, '_'))}`
    }
    return undefined
  })()

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink-0 text-slate-100">
      <Header onToggleMenu={() => setDrawerOpen((value) => !value)} />

      {errorMsg && (
        <div className="absolute left-1/2 top-16 z-50 -translate-x-1/2 rounded-lg border border-red-500/40 bg-red-500/15 px-3 py-2 text-sm text-red-200">
          {errorMsg}
          <button
            type="button"
            className="ml-3 underline"
            onClick={() => selectedId && setSelectedId(selectedId)}
          >
            retry
          </button>
        </div>
      )}

      <div className="absolute inset-0 top-14">
        <Scene
          objectData={objectState.data}
          timeMs={timeMs}
          planets={planets}
          focusPlanet={focusedPlanet}
          orientation="nasa"
          onBackgroundClick={handleSceneBackgroundClick}
        />
      </div>

      <Sidebar
        items={sidebarItems}
        closest={closest}
        loading={isoLoading}
        error={isoError}
        selectedId={selectedId}
        onSelect={handleSelect}
        onSelectClosest={handleSelectClosest}
        onSelectSpecial={handleSelectPlanet}
        onRetry={() => setIsoReloadKey((value) => value + 1)}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <Details
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        id={detailsTargetId}
        wiki={detailsWiki}
      />

      {!detailsOpen && (
        <button
          type="button"
          className="fixed right-4 bottom-28 z-30 rounded-full border border-neutral-800 bg-ink-1/80 px-3 py-1.5 text-sm text-neutral-300 shadow-glow backdrop-blur transition hover:bg-neutral-900 xl:bottom-24"
          onClick={() => setDetailsOpen(true)}
          aria-label="Open details"
        >
          Details
        </button>
      )}

      <Timeline
        timeMs={timeMs}
        onTimeChange={setTimeMs}
        playing={playing}
        onTogglePlay={() => setPlaying(!playing)}
        onScrubStart={() => setPlaying(false)}
      />

      <Toast />
    </div>
  )
}
